import { useQuery, useReactiveVar } from '@apollo/client'
import { FacetValueToCount } from '@deltek/specification-client/core/Models/facet'
import { ListingLevel } from '@deltek/specification-client/core/Models/product'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import { processEnv } from 'components/configuration/environmentVariables'
import { ProductListingsQuery } from 'bpm/api/graphQL/queries/product.queries'
import { ProductListingsResponse } from 'models/product'
import React, { FC, useEffect } from 'react'
import FamilySection from './FamilySection'
import FirmNameAutoCompleteInput from './FirmNameAutoCompleteInput'
import ListingTypeAutoCompleteInput from './ListingTypeAutocompleteInput'
import ProductModelNumberInput from './ProductModelNumberInput'
import ProductNameInput from './ProductNameInput'
import ProductTypeSection from './ProductTypeSection'
import {
    addProductDialogState,
    setCategoriesOptionsState,
    setProductDialogState,
} from './addProductFormHandler'
import { useAddProductFormStyles } from './formStyles'

const isBadgelessCardsEnabled = processEnv.REACT_APP_EnableBadgelessCards === '1'

const AddProductForm: FC = () => {
    const { type } = useReactiveVar(addProductDialogState)

    const classes = useAddProductFormStyles()

    const { loading: listingDataLoading, data: listingData } = useQuery<
        ProductListingsResponse
    >(ProductListingsQuery, {
        variables: {
            listingSearchInput: {},
        },
        fetchPolicy: 'no-cache',
    })

    useEffect(() => {
        if (isBadgelessCardsEnabled) {
            setProductDialogState({ type: ListingLevel.ProductListing })
        }
    }, [])

    useEffect(() => {
        if (listingData) {
            const categoriesObj = (listingData?.listingSearch.facets?.find(
                (facet) => facet.name === 'Categories'
            )?.values || []) as FacetValueToCount[]
            const categoriesArr = categoriesObj.map((c) => c.value)
            setCategoriesOptionsState([
                ...categoriesArr.sort((a: string, b: string) =>
                    a.localeCompare(b, undefined, { sensitivity: 'base' })
                ),
            ])
        }
    }, [listingData, setCategoriesOptionsState])

    return (
        <GridContainer
            data-testid="addProductContainer"
            spacing={2}
            className={classes.addProductContainer}
        >
            {!isBadgelessCardsEnabled && <ListingTypeAutoCompleteInput />}
            {(isBadgelessCardsEnabled || !!type) && (
                <>
                    <FirmNameAutoCompleteInput />
                    <FamilySection isCategoriesLoading={listingDataLoading} />
                    <ProductTypeSection />
                    <ProductModelNumberInput />
                    <ProductNameInput />
                </>
            )}
        </GridContainer>
    )
}

export default AddProductForm
